import { IPassage } from '../../models/Passage';
import { IPassageWaveformSummary } from '../../models/PassageWaveform';
import DeviceService from '../../services/DeviceService';

export interface PassageState {
  selectedPassageTimestamp: string;
  selectedPassages: IPassage[];
  passageDialogSource: string;
  passageIdsToUpdate: number[];
  uidToUpdate: number;
  machineLearningPassageResultList: any[];
}

export default {
  state: {
    selectedPassageTimestamp: '',
    selectedPassages: [] as IPassage[],
    passageDialogSource: 'selected_passage',
    passageIdsToUpdate: [] as number[],
    uidToUpdate: 0,
    machineLearningPassageResultList: [],
  } as PassageState,
  mutations: {
    SET_SELECTED_PASSAGE_TIMESTAMP(state: PassageState, timestamp: string) {
      state.selectedPassageTimestamp = timestamp;
    },
    SET_SELECTED_PASSAGES(state: PassageState, passages: IPassage[]) {
      state.selectedPassages = passages;
    },
    SET_PASSAGE_DIALOG_SOURCE(state: PassageState, source: string) {
      state.passageDialogSource = source;
    },
    ADD_PASSAGE_IDS_TO_UPDATE(state: PassageState, passageIds: number[]) {
      state.passageIdsToUpdate = passageIds;
    },
    ADD_UID_TO_UPDATE(state: PassageState, uid: number) {
      state.uidToUpdate = uid;
    },
    SET_MACHINE_LEARNING_PASSAGE_RESULT_LIST(
      state: PassageState,
      payload: any
    ) {
      state.machineLearningPassageResultList = payload;
    },
  },
  actions: {
    setPassageDialogSource(context: any, payload: string) {
      context.commit('SET_PASSAGE_DIALOG_SOURCE', payload);
    },
    clearPassageIdsToUpdate(context: any) {
      context.commit('ADD_PASSAGE_IDS_TO_UPDATE', []);
    },
    addPassageIdsToUpdate(context: any, passageIds: number[]) {
      context.commit('ADD_PASSAGE_IDS_TO_UPDATE', passageIds);
    },
    addUIDToUpdate(context: any, uid: number) {
      context.commit('ADD_UID_TO_UPDATE', uid);
    },
    updateLastPassage(context: any) {
      const selectedDevice = context.rootState.device.selectedDevice;
      if (!selectedDevice) return;
      DeviceService.getLastPassageData(selectedDevice.uid)
        .then((response: any) => {
          if (response.data && response.data.length) {
            // all last passages will have identical DC and times
            context.commit(
              'SET_DEVICE_PASSAGE_DETECTION_COUNT',
              response.data[0].detectionCount,
              { root: true }
            );
            context.commit(
              'SET_DEVICE_PASSAGE_TIMESTAMP',
              response.data[0].passageTimeStamp,
              { root: true }
            );
            const newSelectedPassages = [];
            for (const passage of response.data) {
              if (passage.packetComplexInformation === 0) {
                passage.elfId = passage.passageId;
              }
              if (passage.packetComplexInformation === 1) {
                passage.geoId = passage.passageId;
              }
              if (passage.packetComplexInformation === 2) {
                passage.magId = passage.passageId;
              }
              newSelectedPassages.push(passage);
            }
            // context.commit('SET_LAST_PASSAGES', newSelectedPassages); // \todo: throws error
          }
        })
        .catch((error: any) => {
          console.error(error);
          context.dispatch('notify', error);
        });
    },
    passageChartShow() {
      // Signal-only action – PassageDialog subscribes and handles the fetch locally.
    },
    passageDownload() {
      // No longer used – DeviceHistory fetches waveform data directly.
    },
    passageExportCsv() {
      // No longer used – DeviceHistory calls onPassageExport directly.
    },
    verifyPassage(
      context: any,
      payload: { passage: any; passageWaveformId: number }
    ) {
      context.dispatch('togglePassageVerification', payload);
    },
    togglePassageVerification(
      context: any,
      payload: { passage: any; passageWaveformId: number }
    ) {
      const { passage, passageWaveformId } = payload;
      if (!passageWaveformId || !passage.uid) {
        console.error('Missing passageWaveformId or uid');
        return;
      }

      const wf = (passage.passageWaveforms || []).find(
        (w: IPassageWaveformSummary) =>
          w.passageWaveformId === passageWaveformId
      );
      if (!wf) {
        console.error('Waveform not found:', passageWaveformId);
        return;
      }

      const newVerified = !wf.isVerified;
      wf.isVerified = newVerified;

      DeviceService.setPassageWaveformVerification(
        passageWaveformId,
        newVerified
      )
        .then(() => {
          if (passage.markerName !== undefined) {
            context.dispatch('verifyMarkerPassageComplete', passage);
          } else {
            context.dispatch('verifyPassageComplete', passage);
            if (context.state.passageDialogSource === 'report_manager') {
              return;
            }
            context.dispatch('updateLastPassage');
          }
        })
        .catch((error: any) => {
          // Revert on failure
          wf.isVerified = !newVerified;
          console.error(error);
          context.dispatch('notify', error);
        });
    },
    unverifyAllPassageWaveforms(context: any, payload: { passage: any }) {
      const { passage } = payload;
      const waveforms: IPassageWaveformSummary[] =
        passage.passageWaveforms || [];

      const verifiedWaveforms = waveforms.filter((wf) => wf.isVerified);
      if (verifiedWaveforms.length === 0) {
        return;
      }

      // Set all to unverified locally
      for (const wf of verifiedWaveforms) {
        wf.isVerified = false;
      }

      const requests = verifiedWaveforms.map((wf) =>
        DeviceService.setPassageWaveformVerification(
          wf.passageWaveformId,
          false
        )
      );

      Promise.allSettled(requests).then((results) => {
        const failedRequests = results.filter(
          (result) => result.status === 'rejected'
        );
        if (failedRequests.length > 0) {
          results.forEach((result, index) => {
            if (result.status === 'rejected') {
              verifiedWaveforms[index].isVerified = true;
            }
          });
          console.error('unverifyAllPassageWaveforms error:', failedRequests);
          context.dispatch('notify', failedRequests[0].reason);
          if (passage.markerName !== undefined) {
            context.dispatch('verifyMarkerPassageComplete', passage);
          }
          return;
        }

        if (passage.markerName !== undefined) {
          context.dispatch('verifyMarkerPassageComplete', passage);
        } else {
          context.dispatch('verifyPassageComplete', passage);
          if (context.state.passageDialogSource === 'report_manager') {
            return;
          }
          context.dispatch('updateLastPassage');
        }
      });
    },
    setPassageWaveformVerification(
      context: any,
      payload: { passage: any; passageWaveformId: number }
    ) {
      const { passage, passageWaveformId } = payload;
      const wf = (passage.passageWaveforms || []).find(
        (w: IPassageWaveformSummary) =>
          w.passageWaveformId === passageWaveformId
      );
      if (passageWaveformId && passage.uid && wf) {
        DeviceService.setPassageWaveformVerification(
          passageWaveformId,
          wf.isVerified
        )
          .then(() => {
            if (passage.markerName !== undefined) {
              context.dispatch('verifyMarkerPassageComplete', passage);
            } else {
              context.dispatch('verifyPassageComplete', passage);
            }
          })
          .catch((error: any) => {
            console.error(error);
            context.dispatch('notify', error);
          });
      } else {
        console.error('Missing passageWaveformId or uid');
      }
    },
    verifyMarkerPassageComplete(context: any, passage: any) {
      const pigRuns = context.rootState.pigRun.pigRuns as Record<number, any>;
      const requestedPigRunId = Number(passage.pigRunId ?? passage.runId);
      const requestedMarkerId = Number(passage.pipelineSectionMarkerId);
      let match: { pigRunId: number; marker: any } | undefined;

      const findMatches = (matches: (candidate: any) => boolean) =>
        Object.keys(pigRuns).flatMap((pigRunId) =>
          (pigRuns[Number(pigRunId)]?.markers || [])
            .filter(matches)
            .map((marker: any) => ({ pigRunId: Number(pigRunId), marker }))
        );

      if (requestedPigRunId > 0) {
        const marker = (pigRuns[requestedPigRunId]?.markers || []).find(
          (candidate: any) =>
            requestedMarkerId > 0
              ? candidate.pipelineSectionMarkerId === requestedMarkerId
              : candidate.name === passage.markerName
        );
        if (marker) {
          match = { pigRunId: requestedPigRunId, marker };
        }
      }

      if (!match && requestedMarkerId > 0) {
        const markerMatches = findMatches(
          (candidate: any) =>
            candidate.pipelineSectionMarkerId === requestedMarkerId
        );
        if (markerMatches.length === 1) {
          match = markerMatches[0];
        }
      }

      if (!match && passage.markerName !== undefined) {
        const markerMatches = findMatches(
          (candidate: any) => candidate.name === passage.markerName
        );
        if (markerMatches.length === 1) {
          match = markerMatches[0];
        }
      }

      if (!match) {
        if (
          requestedPigRunId > 0 &&
          context.rootState.pigRun.selectedCompletedPigRunId ===
            requestedPigRunId
        ) {
          context.commit(
            'REQUEST_COMPLETED_TRACKING_WORKSHEET_REFRESH',
            requestedPigRunId
          );
        } else if (requestedPigRunId > 0 && pigRuns[requestedPigRunId]) {
          return context.dispatch('refreshSinglePigRun', {
            pigRunId: requestedPigRunId,
            force: true,
          });
        }
        return;
      }

      const hasVerified = (passage.passageWaveforms || []).some(
        (waveform: any) => waveform.isVerified
      );
      if (hasVerified) {
        context.commit('UPDATE_MARKER_VERIFICATION', {
          pigRunId: match.pigRunId,
          pipelineSectionMarkerId: match.marker.pipelineSectionMarkerId,
          hasVerifiedPassage: true,
        });
        context.dispatch('refreshVerifiedPassageSources', match.pigRunId);
      }

      return context.dispatch('refreshSinglePigRun', {
        pigRunId: match.pigRunId,
        force: true,
      });
    },
    verifyPassageComplete(_context: any, _payload: any) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    passageRecieved(_context: any, _payload: any) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    mlResultReceived(_context: any, _payload: any) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    completeMachineLearningAddPigRunRequest(_context: any) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
  },
};
